import React, { useState } from "react";
import styled, { DefaultTheme } from "styled-components";

export type AvatarStatus = "online" | "offline" | "busy";

interface AvatarProps {
  src?: string;
  name?: string;
  size?: "xxs" | "xs" | "sm" | "md" | "lg" | "xl";
  status?: AvatarStatus;
  border?: boolean;
  clickable?: boolean;
  className?: string;
  bgColor?: keyof DefaultTheme["colors"] | string; // ✅ Accept theme key or custom color

}

const SIZE_MAP = {
  xxs: 24,
  xs: 28,
  sm: 32,
  md: 40,
  lg: 56,
  xl: 72,
};

const Wrapper = styled.div<{
  $size: number;
  $clickable?: boolean;
  $border?: boolean;
}>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  border: ${({ $border, theme }) =>
    $border ? `2px solid ${theme.colors.primary}` : "none"};
  box-shadow: ${({ theme }) => theme.elevations.low};
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: ${({ $clickable }) => ($clickable ? "scale(1.04)" : "none")};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 50%;
  }
`;

const Initials = styled.span<{ $size: number , $bgColor?: string }>`
  font-weight: ${({ theme }) => theme.fonts.fontWeightBold};
  font-size: ${({ $size }) => Math.floor($size / 2.3)}px;
  color: ${({ theme }) => theme.colors.textonPrimary};
  background: ${({ $bgColor , theme }) => $bgColor ? $bgColor : theme.colors.accent};
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
`;

const Initials2 = styled.span<{ $size: number; $bgColor: string }>`
  font-weight: ${({ theme }) => theme.fonts.fontWeightBold};
  font-size: ${({ $size }) => Math.floor($size / 2.3)}px;
  color: ${({ theme }) => theme.colors.textonPrimary};
  background: ${({ $bgColor , theme}) => $bgColor ? $bgColor : theme.colors.accent};
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
`;

const StatusDot = styled.div<{ status: AvatarStatus }>`
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.surface};
  background-color: ${({ theme, status }) => {
    switch (status) {
      case "online":
        return theme.colors.success;
      case "busy":
        return theme.colors.warning;
      case "offline":
      default:
        return theme.colors.textDisabled;
    }
  }};
`;

export const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  size = "md",
  status,
  border = false,
  clickable = false,
  className,
  bgColor,
}) => {
  const [imgError, setImgError] = useState(false);
  const showInitials = !src || imgError;
  const pixelSize = SIZE_MAP[size];
  const initials =
    name?.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase() || "?";

  return (
    <Wrapper
      className={className}
      $size={pixelSize}
      $clickable={clickable}
      $border={border}
      aria-label={name}
    >
      {showInitials ? (
        <Initials $size={pixelSize} $bgColor={bgColor}>{initials}</Initials>
      ) : (
        <img
          src={src}
          alt={name}
          onError={() => setImgError(true)}
          loading="lazy"
        />
      )}
      {status && <StatusDot status={status} />}
    </Wrapper>
  );
};